import { Component } from 'react';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import Form from 'components/atoms/Form';
import Icon from 'components/atoms/Icon';
import { RootState } from 'types';
import { ActionsType, SystemType } from 'types/system';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { clone, setKey } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface TimeSelectModelProps {
    children?: string | JSX.Element | JSX.Element[];
    language: string;
    system: SystemType | null;
    id?: string;
    data: any;
    actions: ActionsType | undefined;
    dateTimeFormat: DateTimeFormat;
}

interface TimeSelectModelState {
    errors: object;
    data: any | null;
    selectedTime: string;
    sourceTime: string;
}

class TimeSelectModel extends Component<TimeSelectModelProps, TimeSelectModelState> {
    private timerId: NodeJS.Timeout | null = null;
    constructor(props: TimeSelectModelProps) {
        super(props);
        this.state = {
            data: props.data,
            selectedTime: '00:00',
            sourceTime: '00:00',
            errors: {},
        };
    }

    componentDidMount(): void {
        const { data } = this.props;
        const focusIndex = data.foucus;

        // Round to the nearest 30 minutes using Luxon's API
        const roundedTime = data[focusIndex].clone().roundToNearest30Minutes().formatDateTimeToString('HH:mm'); // Format the rounded time

        // Set the state with the rounded time
        this.setState({
            selectedTime: roundedTime,
            sourceTime: roundedTime,
        });
    }

    handleReset = () => {
        this.setTime(this.props.data.foucus === 'endDate' ? '23:59' : '00:00');
    };

    handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
        e?.preventDefault();
        this.props.actions?.onCancel();
    };

    handleFormCancel = async () => {
        await this.setTime(this.state.sourceTime);
        this.props.actions?.onCancel();
    };

    generateTimeSlots = (): string[] => {
        const timeSlots: string[] = [];
        for (let hour = 0; hour < 24; hour++) {
            timeSlots.push(`${hour.toString().padStart(2, '0')}:00`, `${hour.toString().padStart(2, '0')}:30`);
        }
        return timeSlots;
    };

    setTime = (time: string) => {
        const { data } = this.props;
        const focusIndex = data.foucus;

        const [hours, minutes] = time.split(':').map(Number); // Split the input time into hours and minutes

        const filterObject = clone(this.props.data.filterObject);

        if (this.props.data.foucus === 'endDate') {
            setKey(filterObject, 'date.end_utc.op', data[focusIndex].clone().setHoursAndMinutes(hours, minutes));
        } else if (this.props.data.foucus === 'startDate') {
            setKey(filterObject, 'date.start_utc.op', data[focusIndex].clone().setHoursAndMinutes(hours, minutes));
        }
        this.setState(
            {
                selectedTime: time,
            },
            () => {
                this.props.actions?.setFilterObject(filterObject);
            }
        );
    };

    render() {
        return (
            <>
                <Form onSubmit={this.handleFormSubmit} className="bg-white shadow p-4 w-full rounded">
                    <div id={`Drag-${this.props.id}`} className="flex justify-between mb-4 no-select ">
                        <div className="text-lg leading-6 font-bold">{translate('Select time')} </div>
                        <div
                            onTouchEnd={this.handleFormCancel}
                            onClick={this.handleFormCancel}
                            className="cursor-pointer"
                        >
                            <Icon>
                                <path stroke="none" d="M0 0h24v24H0z" fill="none" />
                                <path d="M18 6l-12 12" />
                                <path d="M6 6l12 12" />
                            </Icon>
                        </div>
                    </div>

                    <div className="  gap-4 flex-wrap grid grid-cols-8 ">
                        {this.generateTimeSlots().map((time, index) => (
                            <div
                                onClick={() => {
                                    this.setTime(time);
                                }}
                                className={`${twclsx(
                                    time === this.state.selectedTime && 'bg-gray-300',
                                    'col-span-1',
                                    'cursor-pointer',
                                    'text-center',
                                    'rounded',
                                    'hover:bg-gray-400'
                                )}`}
                                key={index}
                            >
                                {time}
                            </div>
                        ))}
                    </div>

                    <div className="flex justify-end items-center">
                        <div className="mt-5 flex gap-3">
                            <Button
                                onClick={this.handleFormCancel}
                                className="px-4 py-2 font-bold border border-gray-400"
                            >
                                {translate('Cancel')}
                            </Button>
                            <Button type="button" onClick={this.handleReset} className="px-4 py-1 font-bold">
                                {translate('Reset')}
                            </Button>
                            <Button type="submit" className="px-4 py-2 bg-primary-submit rounded-md text-white">
                                {translate('Save')}
                            </Button>
                        </div>
                    </div>
                </Form>
            </>
        );
    }
}

const timeSelectModelStateToProps = (state: RootState) => ({
    language: state.user.user.language,
    system: state.port.system,
    dateTimeFormat: state.port.dateTimeFormat,
});

const timeSelectModelDispatchToProps = () => ({});

export default connect(timeSelectModelStateToProps, timeSelectModelDispatchToProps)(TimeSelectModel);
